import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {useFlagOrCookie} from "@khan/analytics-growthbook";
import type {Step} from "@khan/auth-shared";
import {mediaQueries} from "@khan/shared-styles";

import TermsOfServiceSignupStatement from "../../components/terms-of-service-signup-statement.tsx";
import sharedStyles from "../../shared-styles.ts";

type Props = {
    step: Step;
};

/**
 * JoinDetailScreen renders the "Join your class on Khan Academy" left-hand
 * join page content.
 *
 * We also import it in the Districts join page, for consistency!
 */
const JoinDetailScreen = ({step}: Props): React.ReactNode => {
    const tosCheckboxFeatureFlagOn = useFlagOrCookie(
        "signup-tos-checkbox-required",
    );
    const detailScreenDescriptions = {
        intro: <TermsOfServiceSignupStatement purpose="signup" />,
        confirmClass: <TermsOfServiceSignupStatement purpose="signup" />,
        signUp: <TermsOfServiceSignupStatement purpose="signup" />,
        identify: t`Join Khan Academy to get personalized help with what you’re studying or to learn something completely new. We’ll save all of your progress.`,
        login: <TermsOfServiceSignupStatement purpose="login" />,
    } as const;

    return (
        <div>
            <h1
                className={css(
                    sharedStyles.mainHeading,
                    styles.heading,
                    styles.headingForJoinPageLayout,
                )}
            >
                {t`Join your class on Khan Academy`}
            </h1>

            {/* Hide Terms of Service statement on join page when tosCheckboxFeatureFlagOn is true */}
            {!tosCheckboxFeatureFlagOn && (
                <h3
                    className={css(
                        sharedStyles.description,
                        sharedStyles.subHeadingText,
                    )}
                >
                    {detailScreenDescriptions[step]}
                </h3>
            )}
        </div>
    );
};

const styles = StyleSheet.create({
    heading: {
        [mediaQueries.md]: {
            marginTop: 64,
        },
        [mediaQueries.smOrSmaller]: {
            marginTop: 46,
        },
    },

    // In the new JoinPageLayout, the container controls the spacing here!
    headingForJoinPageLayout: {
        marginTop: 0,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    progressIndicator: {
        marginTop: 24,

        // On small-desktop sizes, add some spacing to avoid having the
        // progress indicator stick to the sides.
        [mediaQueries.lg]: {
            marginLeft: 48,
            marginRight: 48,
        },

        [mediaQueries.lgOrLarger]: {
            // Give it a fixed width, to prevent the size from changing when
            // classes with long names are added. Also, this makes ellipsising
            // the class titles possible.
            width: 340,
        },
    },
    // eslint-disable-next-line react-native/no-unused-styles
    userClassName: {
        // For long titles, ellipsis it so that it doesn't break onto
        // multiple lines.
        display: "block",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
});

export default JoinDetailScreen;
